import * as React from "react";
import { createPortal } from "react-dom";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";

/* ── Tooltip Context ─────────────────────────────────────────────────── */

interface TooltipContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
}

const TooltipContext = React.createContext<TooltipContextValue | null>(null);

function useTooltipContext() {
  const ctx = React.useContext(TooltipContext);
  if (!ctx) throw new Error("Tooltip components must be used within <Tooltip>");
  return ctx;
}

/* ── TooltipProvider ─────────────────────────────────────────────────── */

export interface TooltipProviderProps {
  children: React.ReactNode;
  delayDuration?: number;
}

/** Wrap your app in TooltipProvider to set default tooltip behavior. */
function TooltipProvider({ children }: TooltipProviderProps) {
  return <>{children}</>;
}

/* ── Tooltip Root ────────────────────────────────────────────────────── */

export interface TooltipProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  delayDuration?: number;
}

/** Tooltip root - manages hover state. */
function Tooltip({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  delayDuration = 200,
}: TooltipProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const triggerRef = React.useRef<HTMLElement>(null);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const setOpen = React.useCallback(
    (value: boolean) => {
      if (value) {
        timeoutRef.current = setTimeout(() => {
          if (controlledOpen === undefined) setInternalOpen(true);
          onOpenChange?.(true);
        }, delayDuration);
      } else {
        clearTimeout(timeoutRef.current);
        if (controlledOpen === undefined) setInternalOpen(false);
        onOpenChange?.(false);
      }
    },
    [controlledOpen, onOpenChange, delayDuration],
  );

  React.useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  return (
    <TooltipContext.Provider value={{ open, setOpen, triggerRef }}>
      {children}
    </TooltipContext.Provider>
  );
}

/* ── TooltipTrigger ──────────────────────────────────────────────────── */

export interface TooltipTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const TooltipTrigger = React.forwardRef<HTMLButtonElement, TooltipTriggerProps>(
  ({ className, asChild = false, onMouseEnter, onMouseLeave, onFocus, onBlur, ...props }, forwardedRef) => {
    const { setOpen, triggerRef } = useTooltipContext();

    const setRefs = (node: HTMLElement | null) => {
      triggerRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node as HTMLButtonElement);
      else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLButtonElement | null>).current = node as HTMLButtonElement;
      }
    };

    const handleMouseEnter: React.MouseEventHandler<HTMLElement> = (e) => {
      setOpen(true);
      onMouseEnter?.(e as React.MouseEvent<HTMLButtonElement>);
    };

    const handleMouseLeave: React.MouseEventHandler<HTMLElement> = (e) => {
      setOpen(false);
      onMouseLeave?.(e as React.MouseEvent<HTMLButtonElement>);
    };

    const handleFocus: React.FocusEventHandler<HTMLElement> = (e) => {
      setOpen(true);
      onFocus?.(e as React.FocusEvent<HTMLButtonElement>);
    };

    const handleBlur: React.FocusEventHandler<HTMLElement> = (e) => {
      setOpen(false);
      onBlur?.(e as React.FocusEvent<HTMLButtonElement>);
    };

    if (asChild && React.isValidElement(props.children)) {
      const child = props.children as React.ReactElement<Record<string, unknown>>;
      const childProps = (child.props ?? {}) as Record<string, unknown>;

      return React.cloneElement(child, {
        ref: setRefs,
        onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
          handleMouseEnter(e);
          (childProps.onMouseEnter as React.MouseEventHandler<HTMLElement> | undefined)?.(e);
        },
        onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
          handleMouseLeave(e);
          (childProps.onMouseLeave as React.MouseEventHandler<HTMLElement> | undefined)?.(e);
        },
        onFocus: (e: React.FocusEvent<HTMLElement>) => {
          handleFocus(e);
          (childProps.onFocus as React.FocusEventHandler<HTMLElement> | undefined)?.(e);
        },
        onBlur: (e: React.FocusEvent<HTMLElement>) => {
          handleBlur(e);
          (childProps.onBlur as React.FocusEventHandler<HTMLElement> | undefined)?.(e);
        },
        className: cn(childProps.className as string | undefined, className),
      });
    }

    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={setRefs}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(className)}
        {...(asChild ? props : { type: "button", ...props })}
      />
    );
  },
);
TooltipTrigger.displayName = "TooltipTrigger";

/* ── TooltipContent ──────────────────────────────────────────────────── */

export interface TooltipContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
}

function getTooltipPosition(
  rect: DOMRect,
  side: NonNullable<TooltipContentProps["side"]>,
  sideOffset: number,
) {
  switch (side) {
    case "right":
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + sideOffset,
        transform: "translateY(-50%)",
      };
    case "left":
      return {
        top: rect.top + rect.height / 2,
        left: rect.left - sideOffset,
        transform: "translate(-100%, -50%)",
      };
    case "bottom":
      return {
        top: rect.bottom + sideOffset,
        left: rect.left + rect.width / 2,
        transform: "translateX(-50%)",
      };
    case "top":
    default:
      return {
        top: rect.top - sideOffset,
        left: rect.left + rect.width / 2,
        transform: "translate(-50%, -100%)",
      };
  }
}

const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(
  ({ className, side = "top", sideOffset = 8, style, ...props }, ref) => {
    const { open, triggerRef } = useTooltipContext();
    const [position, setPosition] = React.useState({ top: 0, left: 0, transform: "" });

    React.useLayoutEffect(() => {
      if (!open || !triggerRef.current) return;

      const update = () => {
        const rect = triggerRef.current!.getBoundingClientRect();
        setPosition(getTooltipPosition(rect, side, sideOffset));
      };

      update();
      const raf = requestAnimationFrame(update);
      window.addEventListener("scroll", update, true);
      window.addEventListener("resize", update);
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("scroll", update, true);
        window.removeEventListener("resize", update);
      };
    }, [open, triggerRef, side, sideOffset]);

    if (!open) return null;

    return createPortal(
      <div
        ref={ref}
        role="tooltip"
        style={{
          position: "fixed",
          top: position.top,
          left: position.left,
          transform: position.transform,
          zIndex: 9999,
          ...style,
        }}
        className={cn(
          "pointer-events-none max-w-xs rounded-lg px-3 py-1.5 text-xs font-semibold whitespace-nowrap",
          "bg-slate-900 text-white border border-white/10 shadow-xl animate-fade-in",
          className,
        )}
        {...props}
      />,
      document.body,
    );
  },
);
TooltipContent.displayName = "TooltipContent";

export { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent };
